// SharedView — read-only rendering of a shared project at /share/:code.
// No auth required. Fetches from GET /api/share/:code.

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Project } from '../types';
import { ResultsContent } from './ResultsPage';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; project: Project };

export function SharedView() {
  const { code } = useParams<{ code: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!code) {
      setState({ status: 'error', message: 'No share code provided.' });
      return;
    }

    let cancelled = false;

    fetch(`/api/share/${code}`)
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<Project>;
      })
      .then(project => {
        if (!cancelled) setState({ status: 'ok', project });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      });

    return () => { cancelled = true; };
  }, [code]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Minimal read-only header */}
      <header style={{
        backgroundColor: '#1e293b',
        borderBottom: '1px solid #334155',
        padding: '0 24px',
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '28px',
            height: '28px',
            backgroundColor: '#132840',
            border: '1px solid rgba(245,166,33,0.5)',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 700,
            color: '#F5A621',
          }}>
            EEN
          </div>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>
            VM Sizing Tool
          </span>
          <span style={{
            padding: '2px 8px',
            backgroundColor: '#1e3a5f',
            color: '#93c5fd',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
          }}>
            Shared View
          </span>
        </div>
        <Link
          to="/"
          style={{
            fontSize: '12px',
            color: '#64748b',
            textDecoration: 'none',
          }}
        >
          Open tool →
        </Link>
      </header>

      {/* Main content */}
      <main style={{ padding: '32px 24px', maxWidth: '1280px', margin: '0 auto' }}>
        {state.status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '80px 24px', color: '#64748b' }}>
            Loading shared project…
          </div>
        )}

        {state.status === 'error' && (
          <div style={{
            maxWidth: 480,
            margin: '80px auto',
            textAlign: 'center',
            padding: '40px 32px',
            border: '1px solid #fca5a5',
            borderRadius: '10px',
            backgroundColor: '#fef2f2',
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', color: '#991b1b' }}>
              Project not found
            </h2>
            <p style={{ margin: '0', fontSize: '14px', color: '#b91c1c' }}>
              {state.message}
            </p>
            <p style={{ margin: '16px 0 0', fontSize: '13px', color: '#6b7280' }}>
              The share link may have expired or the project may have been deleted.
            </p>
          </div>
        )}

        {state.status === 'ok' && (
          <ResultsContent project={state.project} />
        )}
      </main>
    </div>
  );
}
